import cx from "classnames";
import { memo, ReactNode } from "react";
import { Checkbox, CheckboxProps, Switch, SwitchProps } from "react-aria-components";

import { FlexAlign } from "types/Flex";
import { withTestId } from "utils/withTestId";

import styles from "./styles.module.css";
import ToggleContent from "./Content";
import { TypographyProps } from "../Typography";

type ToggleSharedProps = {
  id?: string;
  color?: "default" | "inversed";
  className?: string;
  children?: ReactNode;
  align?: FlexAlign;
  tooltip?: (isSelected: boolean) => string;
  initialMinHeight?: boolean;
  typographyVariant?: TypographyProps["variant"];
  "data-testid"?: string;
};

type ToggleProps =
  | (ToggleSharedProps & CheckboxProps & { variant: "checkbox" })
  | (ToggleSharedProps & SwitchProps & { variant: "switch" });

/**
 * - Checkbox variant
 *   - Used within forms to indicate boolean state of form fields, requires confirmation by saving the form
 *   - Renders react-aria's Checkbox, so it announces as a checkbox and supports an indeterminate state
 * - Switch variant
 *   - Used to trigger immediate interaction (toggling states)
 *   - Used in forms to hide/show form sections
 *   - Renders react-aria's Switch, so it announces as a switch. It has no indeterminate state.
 */
const Toggle = ({
  children,
  className,
  color,
  align,
  variant,
  tooltip,
  initialMinHeight,
  typographyVariant,
  "data-testid": dataTestId,
  ...rest
}: ToggleProps) => {
  // When no aria-label is provided and there are no children to act as a label,
  // we need to provide a default aria-label for screen readers
  const ariaLabel =
    rest["aria-label"] || (children ? undefined : `Toggle ${rest.isSelected ? "on" : "off"}`);

  const renderContent = ({
    isDisabled,
    isSelected,
    isIndeterminate = false,
  }: {
    isDisabled: boolean;
    isSelected: boolean;
    isIndeterminate?: boolean;
  }) => (
    <ToggleContent
      color={color}
      align={align}
      variant={variant}
      isDisabled={isDisabled}
      isIndeterminate={isIndeterminate}
      isSelected={isSelected}
      tooltip={tooltip?.(isSelected || isIndeterminate)}
      {...withTestId(dataTestId)}
      initialMinHeight={initialMinHeight}
      typographyVariant={typographyVariant}
    >
      {children}
    </ToggleContent>
  );

  if (variant === "switch") {
    return (
      <Switch
        {...(rest as SwitchProps)}
        aria-label={ariaLabel}
        className={cx(styles.toggleWrapper, className)}
      >
        {renderContent}
      </Switch>
    );
  }

  const checkboxProps = rest as CheckboxProps;

  return (
    <Checkbox
      {...checkboxProps}
      aria-label={ariaLabel}
      className={cx(styles.toggleWrapper, className)}
      data-indeterminate={checkboxProps.isIndeterminate}
    >
      {renderContent}
    </Checkbox>
  );
};

Toggle.displayName = "DS.Toggle";

export default memo(Toggle);
